// A small completion ring (DaisyUI radial-progress) in the app blue, with a
// label in the middle. Used for course completion in the Academy; the same
// element fits anywhere a percentage needs a glance.
//
//   <ProgressRing value={60} size="sm" />              // "60%" inside
//   <ProgressRing value={60} size="lg">3/5</ProgressRing>
import { CSSProperties, ReactNode } from "react";
import cn from "classnames";

const SIZES = {
  xs: { size: "2rem", thickness: "3px", text: "text-[9px]" },
  sm: { size: "2.75rem", thickness: "4px", text: "text-[10px]" },
  md: { size: "4rem", thickness: "5px", text: "text-xs" },
  lg: { size: "5.5rem", thickness: "6px", text: "text-sm" },
} as const;

interface Props {
  /** 0–100. */
  value: number;
  size?: keyof typeof SIZES;
  /** Replaces the default "N%" label. */
  children?: ReactNode;
  className?: string;
  title?: string;
}

const ProgressRing: React.FC<Props> = ({ value, size = "sm", children, className, title }) => {
  const v = Math.max(0, Math.min(100, Math.round(value)));
  const s = SIZES[size];
  return (
    <div
      role="progressbar"
      aria-valuenow={v}
      aria-valuemin={0}
      aria-valuemax={100}
      title={title ?? `${v}% complete`}
      className={cn("radial-progress text-blue-500 bg-base-200 border-0 shrink-0", className)}
      style={{ "--value": v, "--size": s.size, "--thickness": s.thickness } as CSSProperties}
    >
      <span className={cn("font-semibold tabular-nums text-base-content", s.text)}>{children ?? `${v}%`}</span>
    </div>
  );
};

export default ProgressRing;
